/**
 * Wizard step — Telemetry Channels selection for a new customer.
 *
 * Business profile presets:
 *   PHARMA     → temperature + humidity + GPS + bag events
 *   DIAGNOSTIC → temperature + GPS + bag events
 *   BULLION    → weight + GPS + bag events
 *   DEMO       → all five channels (used for showcase tenants)
 *   CUSTOM     → admin toggles channels manually
 *
 * Result is stored on `formData.profile_type` and `formData.telemetry_channels`.
 */
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  Pill, Microscope, Gem, Sparkles, SlidersHorizontal, Landmark,
  Thermometer, Droplets, MapPin, PackageOpen, Scale,
} from 'lucide-react';

export const PROFILE_PRESETS = {
  PHARMA:     { temperature: true,  humidity: true,  gps: true, bag_events: true,  weight: false },
  DIAGNOSTIC: { temperature: true,  humidity: false, gps: true, bag_events: true,  weight: false },
  BULLION:    { temperature: false, humidity: false, gps: true, bag_events: true,  weight: true  },
  BANK:       { temperature: false, humidity: false, gps: true, bag_events: false, weight: false },
  BANK_P2:    { temperature: false, humidity: false, gps: true, bag_events: true,  weight: false },
  DEMO:       { temperature: true,  humidity: true,  gps: true, bag_events: true,  weight: true  },
  CUSTOM:     { temperature: true,  humidity: false, gps: true, bag_events: true,  weight: false },
};

const PROFILES = [
  { id: 'PHARMA',     label: 'Pharmaceutical',        icon: Pill,               description: 'Temperature + humidity + GPS + bag events' },
  { id: 'DIAGNOSTIC', label: 'Diagnostic Lab',        icon: Microscope,         description: 'Temperature + GPS + bag events' },
  { id: 'BULLION',    label: 'Bullion / High-Value',  icon: Gem,                description: 'Weight + GPS + bag events' },
  { id: 'BANK',       label: 'Bank (CIT Phase 1)',    icon: Landmark,           description: 'Live GPS, KM verification, trip & halt history' },
  { id: 'BANK_P2',    label: 'Bank (CIT Phase 2)',    icon: Landmark,           description: 'Phase 1 + container open/close events at geo-location' },
  { id: 'DEMO',       label: 'Demo (All Channels)',   icon: Sparkles,           description: 'Every telemetry channel enabled' },
  { id: 'CUSTOM',     label: 'Custom',                icon: SlidersHorizontal,  description: 'Pick channels manually' },
];

const CHANNELS = [
  { key: 'temperature', label: 'Temperature',         icon: Thermometer, hint: '°C readings every 5 min' },
  { key: 'humidity',    label: 'Humidity',            icon: Droplets,    hint: 'Relative humidity %' },
  { key: 'gps',         label: 'GPS Location',        icon: MapPin,      hint: 'Live trail + last position' },
  { key: 'bag_events',  label: 'Bag Open / Close',    icon: PackageOpen, hint: 'Tamper events with timestamp' },
  { key: 'weight',      label: 'Weight + Delta',      icon: Scale,       hint: 'Weight readings + delivery deltas' },
];

export function CustomerStepTelemetry({ formData, onChange }) {
  const profile = formData.profile_type || 'PHARMA';
  const channels = formData.telemetry_channels || PROFILE_PRESETS[profile];

  const setProfile = (next) => {
    onChange('profile_type', next);
    // Always apply preset channels when profile changes; admin can toggle after.
    onChange('telemetry_channels', { ...PROFILE_PRESETS[next] });
  };

  const toggleChannel = (key) => {
    const updated = { ...channels, [key]: !channels[key] };
    onChange('telemetry_channels', updated);
    // Switch to CUSTOM if the admin's toggle no longer matches the preset.
    const preset = PROFILE_PRESETS[profile];
    const matches = Object.keys(preset).every((k) => preset[k] === updated[k]);
    if (!matches) onChange('profile_type', 'CUSTOM');
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
          Select the customer's business profile. This drives which telemetry channels
          their dashboard exposes.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {PROFILES.map((p) => {
            const Icon = p.icon;
            const selected = profile === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setProfile(p.id)}
                data-testid={`profile-${p.id.toLowerCase()}`}
                className={cn(
                  'text-left p-3 rounded-lg border transition-all',
                  selected
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                    : 'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-600',
                )}
              >
                <div className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center mb-2',
                  selected ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-300',
                )}>
                  <Icon className="w-4 h-4" />
                </div>
                <p className={cn(
                  'text-sm font-medium',
                  selected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-200',
                )}>
                  {p.label}
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">{p.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <Label className="text-slate-700 dark:text-slate-200 text-sm font-semibold">Telemetry channels</Label>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            Toggles override the preset and switch the profile to CUSTOM.
          </p>
        </div>
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {CHANNELS.map((ch) => {
            const Icon = ch.icon;
            const on = !!channels[ch.key];
            return (
              <label
                key={ch.key}
                className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-slate-100/60 dark:hover:bg-slate-800/60"
                data-testid={`channel-row-${ch.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center',
                    on ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'bg-slate-200 dark:bg-slate-700 text-slate-400',
                  )}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className={cn('text-sm font-medium', on ? 'text-slate-900 dark:text-white' : 'text-slate-500')}>{ch.label}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{ch.hint}</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleChannel(ch.key)}
                  className="w-5 h-5 rounded accent-blue-600 cursor-pointer"
                  data-testid={`channel-toggle-${ch.key}`}
                />
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
